import { Link } from 'react-router-dom';
import { siteConfig } from '@/mocks/site';
import { voices } from '@/mocks/voices';
import { categories } from '@/mocks/categories';
import { useSeo } from '@/hooks/useSeo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import SectionHeading from '@/components/base/SectionHeading';
import { themeSoft } from '@/utils/theme';
import type { ThemeKey } from '@/types/content';

const principles = [
  {
    icon: 'ri-git-branch-line',
    theme: 'primary' as ThemeKey,
    title: '多信源交叉',
    desc: '任何一条结论，至少要有两个互相独立的信源指向同一方向，才会以“已核实”的状态呈现。',
  },
  {
    icon: 'ri-shield-user-line',
    theme: 'secondary' as ThemeKey,
    title: '保护当事人',
    desc: '对非公开发言人与普通当事人做匿名化处理，不公开与公共利益无关的私人细节。',
  },
  {
    icon: 'ri-calendar-check-line',
    theme: 'accent' as ThemeKey,
    title: '时间线优先',
    desc: '用时间线组织信息，让读者能自己看清前因后果，而不是被一句结论带着走。',
  },
  {
    icon: 'ri-refresh-line',
    theme: 'primary' as ThemeKey,
    title: '持续更新',
    desc: '事件有进展时更新原文并标注时间，而不是重复发一篇新的标题党内容。',
  },
];

const milestones = [
  { year: '起点', title: '从一个投稿邮箱开始', desc: '最初只是几位编辑在整理读者发来的线索，希望把零散的信息讲清楚。' },
  { year: '成型', title: '建立五个固定栏目', desc: '按娱乐、科技、财经、民生、生活拆分条线，每条线由固定编辑跟进。' },
  { year: '现在', title: '核实状态公开标注', desc: '在文章里明确区分已核实、多方信源与待回应，把判断权交还给读者。' },
];

export default function AboutPage() {
  useSeo({
    title: '关于我们 - 17爆料网',
    description:
      '了解17爆料网的定位、内容原则与发展理念。我们聚焦未公开信息与内部消息，用多信源交叉与时间线还原事件脉络。',
    keywords: '17爆料网,关于17爆料网,17爆料网内容原则,17爆料网知情人士',
    path: '/about',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 17爆料网',
        url: 'https://dyfscl.cn/about',
        description: '17爆料网的定位、内容原则与发展理念。',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteConfig.name,
        url: 'https://dyfscl.cn/',
        email: siteConfig.email,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://dyfscl.cn/' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://dyfscl.cn/about' },
        ],
      },
    ],
  });

  return (
    <>
      <section className="bg-background-100 border-b border-background-200">
        <div className="px-4 md:px-8 pt-6 pb-12">
          <Breadcrumbs items={[{ label: '关于我们' }]} />
          <div className="mt-8 grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 text-xs tracking-widest text-primary-700 font-label mb-4">
                <span className="w-6 h-px bg-primary-500"></span>
                ABOUT US
              </span>
              <h1 className="font-heading font-bold text-3xl md:text-4xl leading-tight text-foreground-950">
                我们只做一件事：把线索讲清楚
              </h1>
              <p className="mt-5 text-sm md:text-base text-foreground-600 leading-relaxed">
                {siteConfig.name}聚焦未公开信息与内部消息，汇集知情人士爆料与第一手料整理。我们不追热闹，
                而是把零散的说法按时间排好、按信源分层，让你看到别处没有的细节，也让你自己判断哪些能信。
              </p>
            </div>
            <div className="lg:col-span-5">
              <dl className="grid grid-cols-3 gap-4 border-t border-background-300 pt-6 lg:border-t-0 lg:pt-0">
                <div>
                  <dt className="font-heading font-bold text-2xl text-foreground-950">5</dt>
                  <dd className="mt-1 text-xs text-foreground-500">固定栏目</dd>
                </div>
                <div>
                  <dt className="font-heading font-bold text-2xl text-foreground-950">3 级</dt>
                  <dd className="mt-1 text-xs text-foreground-500">信源标注</dd>
                </div>
                <div>
                  <dt className="font-heading font-bold text-2xl text-foreground-950">每日</dt>
                  <dd className="mt-1 text-xs text-foreground-500">更新频率</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          eyebrow="PRINCIPLES"
          title="内容原则"
          desc="这四条是我们在处理每一条线索时都会问自己的问题。"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {principles.map((p) => (
            <div key={p.title} className="p-6 rounded-lg border border-background-200 bg-background-50">
              <span className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl mb-4 ${themeSoft[p.theme]}`}>
                <i className={p.icon}></i>
              </span>
              <h3 className="font-heading font-bold text-lg text-foreground-950">{p.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="px-4 md:px-8 py-12 md:py-16">
          <SectionHeading eyebrow="OUR STORY" title="我们怎么走到今天" desc="从一封投稿邮件，到一个有固定条线的内容平台。" />
          <div className="relative border-l border-background-300 ml-2 space-y-8">
            {milestones.map((m, i) => (
              <div key={m.year} className="relative pl-8">
                <span
                  className={`absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full border-2 border-background-100 ${
                    i === 0 ? 'bg-primary-500' : 'bg-secondary-500'
                  }`}
                ></span>
                <span className="text-xs font-label tracking-widest text-primary-700">{m.year}</span>
                <h3 className="mt-1.5 font-heading font-bold text-lg text-foreground-950">{m.title}</h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-2xl">{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16">
        <SectionHeading eyebrow="COLUMNS" title="我们覆盖哪些领域" desc="每个栏目都有自己的编辑，保证事件脉络连续可追。" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((c) => (
            <Link
              key={c.slug}
              to={`/category/${c.slug}`}
              className="group p-5 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <span className={`w-10 h-10 rounded-md flex items-center justify-center text-xl mb-3 ${themeSoft[c.theme as ThemeKey]}`}>
                <i className={c.icon}></i>
              </span>
              <h3 className="font-heading font-bold text-base text-foreground-950 group-hover:text-primary-700 transition-colors">
                {c.name}
              </h3>
              <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed line-clamp-2">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="px-4 md:px-8 py-12 md:py-16">
          <SectionHeading eyebrow="READERS" title="读者口碑" desc="这些话来自长期读者与行业从业者的反馈。" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {voices.map((v) => (
              <figure key={v.name} className="p-6 rounded-lg bg-background-50 border border-background-200">
                <blockquote className="text-sm md:text-base text-foreground-700 leading-relaxed">{v.text}</blockquote>
                <figcaption className="mt-4 flex items-center gap-3">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center font-heading font-bold text-sm ${themeSoft[v.theme as ThemeKey]}`}>
                    {v.name.slice(0, 1)}
                  </span>
                  <span className="text-sm text-foreground-950">
                    {v.name}
                    <span className="text-foreground-500"> · {v.title}</span>
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-8 py-12 md:py-16">
        <div className="rounded-lg bg-accent-50 border border-accent-200 p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">有线索，或者想聊聊？</h2>
            <p className="mt-2 text-sm text-foreground-600 leading-relaxed max-w-xl">
              投稿、纠错、转载合作都可以。邮箱 {siteConfig.email}，或者直接使用投稿入口。
            </p>
          </div>
          <Link
            to="/contact"
            className="shrink-0 inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-send-plane-line"></i>
            前往投稿
          </Link>
        </div>
      </section>
    </>
  );
}